import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import { 
    Search, 
    Mail, 
    MapPin, 
    FileText, 
    Calendar, 
    Camera, 
    Bell,
    ChevronLeft,
    ChevronRight
} from "lucide-react";
import { getQuoteClients } from "../../redux/Actions/AdminAction";
import { RootState } from "../../redux/store";
import NotificationModal from "./modals/NotificationModal";
// Client Card Component
const ClientCard = ({ client, onNotifyClick }) => (
    <Card className="hover:shadow-md transition-shadow" style={{ textAlign: "left" }}>
        <div className="p-6 space-y-4">
            <div className="flex justify-between items-start">
                <div>
                    <h3 className="font-medium">{client.firstName} {client.lastName}</h3>
                    <div className="flex flex-wrap gap-2 mt-1 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                            <Mail className="h-4 w-4" />
                            {client.email}
                        </span>
                        <span className="flex items-center gap-1">
                            <MapPin className="h-4 w-4" />
                            {client.address || "N/A"}
                        </span>
                    </div>
                </div>
                <Badge variant={undefined} className={client.status?.id === 1 ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}>
                    {client.status?.name || "Unknown"}
                </Badge>
            </div>
            <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-gray-500" />
                            <span className="text-sm">Quote Requests</span>
                        </div>
                        <Badge variant="outline">{client.quoteJobCount || 0}</Badge>
                    </div>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-gray-500" />
                            <span className="text-sm">Joined</span>
                        </div>
                        <span className="text-sm">{new Date(client.createdAt).toLocaleDateString()}</span>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Camera className="h-4 w-4 text-gray-500" />
                            <span className="text-sm">Camera Access</span>
                        </div>
                        <Badge variant={undefined} className={client.permissions?.camera ? "bg-green-100 text-green-700 border-green-200" : "bg-red-100 text-red-700 border-red-200"}>
                            {client.permissions?.camera ? "Enabled" : "Disabled"}
                        </Badge>
                    </div>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Bell className="h-4 w-4 text-gray-500" />
                            <span className="text-sm">Notifications</span>
                        </div>
                        <Badge variant={undefined} className={client.permissions?.notifications ? "bg-green-100 text-green-700 border-green-200" : "bg-red-100 text-red-700 border-red-200"}>
                            {client.permissions?.notifications ? "Enabled" : "Disabled"}
                        </Badge>
                    </div>
                </div>
            </div>

            <div className="pt-4 border-t flex justify-end gap-2">
                <Button
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-1"
                    onClick={() => onNotifyClick(client)}
                >
                    <Bell className="h-4 w-4" />
                    Notify
                </Button>
            </div>
        </div>
    </Card>
);

// Clients Tab Content
const QuoteClientTab = () => {
    const dispatch = useDispatch();
    const { quoteClients, error } = useSelector((state: RootState) => state.admin);
    
    const [searchClientQuery, setSearchClientQuery] = useState("");
    const [selectedStatus, setSelectedStatus] = useState("all");
    const [currentPage, setCurrentPage] = useState(1);
    const [limit] = useState(10);
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
    const [showNotificationModal, setShowNotificationModal] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);
    
    // Debounce search query
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(searchClientQuery);
        }, 500);

        return () => clearTimeout(timer);
    }, [searchClientQuery]);
    
    // Fetch clients when component mounts or when filters change
    const fetchClients = useCallback(() => {
        dispatch(getQuoteClients({
            page: currentPage,
            limit,
            search: debouncedSearchQuery,
            status: selectedStatus,
            isShowLoader: true
        }) as any);
    }, [dispatch, currentPage, limit, debouncedSearchQuery, selectedStatus]);

    useEffect(() => {
        fetchClients();
    }, [fetchClients]);

    // Reset to first page when search or status changes
    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearchQuery, selectedStatus]);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handleNotifyClick = (client: any) => {
        setSelectedClient(client);
        setShowNotificationModal(true);
    };

    const handleCloseNotificationModal = () => {
        setShowNotificationModal(false);
        setSelectedClient(null);
    };

    const renderPagination = () => {
        if (quoteClients.totalPages <= 1) return null;

        const pages = [];
        const totalPages = quoteClients.totalPages;
        const currentPageNum = quoteClients.currentPage;

        // Show previous button
        if (currentPageNum > 1) {
            pages.push(
                <button
                    key="prev"
                    onClick={() => handlePageChange(currentPageNum - 1)}
                    className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                    <ChevronLeft className="h-4 w-4" />
                </button>
            );
        }

        // Show page numbers
        const startPage = Math.max(1, currentPageNum - 2);
        const endPage = Math.min(totalPages, currentPageNum + 2);

        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <button
                    key={i}
                    onClick={() => handlePageChange(i)}
                    className={`px-3 py-2 border rounded-md ${
                        i === currentPageNum
                            ? "bg-blue-500 text-white border-blue-500"
                            : "border-gray-300 hover:bg-gray-50"
                    }`}
                >
                    {i}
                </button>
            );
        }

        // Show next button
        if (currentPageNum < totalPages) {
            pages.push(
                <button
                    key="next"
                    onClick={() => handlePageChange(currentPageNum + 1)}
                    className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                    <ChevronRight className="h-4 w-4" />
                </button>
            );
        }

        return (
            <div className="flex items-center justify-center gap-2 mt-6">
                {pages}
            </div>
        );
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row justify-between gap-4">
                <div className="flex flex-col sm:flex-row gap-4 flex-1">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search clients..."
                            value={searchClientQuery}
                            onChange={(e) => setSearchClientQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>
                    <select
                        value={selectedStatus}
                        onChange={(e) => setSelectedStatus(e.target.value)}
                        className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                        <option value="all">All Statuses</option>
                        <option value="1">Active</option>
                        <option value="2">Inactive</option>
                        <option value="3">Suspended</option>
                        <option value="4">Deleted</option>
                    </select>
                </div>
            </div>

            {/* Results count */}
            <div className="flex justify-between items-center">
                <p className="text-sm text-gray-600">
                    Showing {quoteClients.data.length} of {quoteClients.totalCount} clients
                </p>
                {debouncedSearchQuery && (
                    <p className="text-sm text-gray-500">
                        Search results for: {debouncedSearchQuery}
                    </p>
                )}
            </div>

            {/* Error message */}
            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                    Error loading clients: {error}
                </div>
            )}

            {/* Loading state */}
            {quoteClients.data.length === 0 && !error && (
                <div className="text-center py-8">
                    <p className="text-gray-500">No clients found.</p>
                </div>
            )}

            {/* Clients grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {quoteClients.data.map(client => (
                    <ClientCard key={client.id} client={client} onNotifyClick={handleNotifyClick} />
                ))}
            </div>

            {/* Pagination */}
            {renderPagination()}

            {/* Notification Modal */}
            <NotificationModal
                isOpen={showNotificationModal}
                onClose={handleCloseNotificationModal}
                client={selectedClient}
            />
        </div>
    );
};

export default QuoteClientTab;